import { type ImportSheetDescriptor } from '@utazzo/shared';
import { UzAlert, UzBadge, UzButton, UzCard, UzSelect, UzSpinner } from '@utazzo/ui';
import {
  AlertCircle,
  ArrowLeft,
  Check,
  Cloud,
  Download,
  FileSpreadsheet,
  RefreshCw,
  type LucideIcon,
} from 'lucide-react';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { PageShell } from '@/components/page-shell';
import { JobSummary } from '@/features/lead-imports/components/JobSummary';
import { OperatorGuideButton } from '@/features/lead-imports/components/OperatorGuideButton';
import { RowCard } from '@/features/lead-imports/components/RowCard';
import { SampleTemplateButton } from '@/features/lead-imports/components/SampleTemplateButton';
import {
  useCommitImport,
  useImportJob,
  useImportModules,
  useImportRows,
  useImportSourceFile,
  useRevalidateImport,
  useUploadImport,
} from '@/features/lead-imports/use-imports';
import { useListReturn } from '@/hooks/use-list-return';

/**
 * Generic multi-sheet importer wizard (ADR-0036 v2). Used by Visas /
 * Activities etc. - any importer that declares `sheets`. Each thin
 * per-module page provides the moduleKey + branding + per-sheet summary
 * field hints.
 */
export interface MultiSheetImportPageConfig {
  moduleKey: string;
  titleSingular: string;
  titlePlural: string;
  routePrefix: string;
  listPath: string;
  ListIcon: LucideIcon;
  invalidateOnSuccess: string[];
  /** sheetKey → field ids to surface on collapsed row headers. */
  summaryFieldsBySheet?: Record<string, ReadonlyArray<string>>;
}

export const MultiSheetImportPage = ({
  config,
}: {
  config: MultiSheetImportPageConfig;
}): React.JSX.Element => {
  const { jobId } = useParams<{ jobId?: string }>();
  if (!jobId) return <UploadStep config={config} />;
  return <ReviewStep jobId={jobId} config={config} />;
};

const UploadStep = ({ config }: { config: MultiSheetImportPageConfig }): React.JSX.Element => {
  const navigate = useNavigate();
  const backToList = useListReturn(config.listPath);
  const modulesQuery = useImportModules();
  const upload = useUploadImport();
  const fileRef = useRef<HTMLInputElement | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const activeModule = (modulesQuery.data ?? []).find((m) => m.moduleKey === config.moduleKey);
  const sheets = activeModule?.sheets ?? [];
  const ListIcon = config.ListIcon;

  const handleUpload = async (): Promise<void> => {
    if (!file) return;
    const { jobId } = await upload.mutateAsync({ moduleKey: config.moduleKey, file });
    navigate(`/${config.routePrefix}/imports/jobs/${jobId}`);
  };

  return (
    <PageShell
      title={`Import ${config.titlePlural}`}
      description={`Upload a multi-sheet XLSX. Parent ${config.titlePlural} live on the first sheet; child rows on the rest.`}
      actions={
        <div className="flex gap-2">
          <UzButton variant="ghost" size="sm" onClick={backToList}>
            <ListIcon className="mr-1.5 h-4 w-4" /> {config.titlePlural} list
          </UzButton>
          <UzButton
            variant="ghost"
            size="sm"
            onClick={() => navigate(`/${config.routePrefix}/imports`)}
          >
            <ArrowLeft className="mr-1.5 h-4 w-4" /> Audit log
          </UzButton>
        </div>
      }
    >
      <div className="mx-auto max-w-3xl space-y-4">
        <UzCard
          title="XLSX file"
          description="Max 10MB. Workbook must contain the sheets listed below."
        >
          <input
            ref={fileRef}
            type="file"
            accept=".xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            className="hidden"
            onChange={(e) => setFile(e.target.files?.[0] ?? null)}
          />
          <div className="flex items-center gap-3">
            <UzButton variant="outline" onClick={() => fileRef.current?.click()}>
              <FileSpreadsheet className="mr-1.5 h-4 w-4" />
              Pick XLSX
            </UzButton>
            <div className="text-sm text-zinc-700">
              {file ? (
                <>
                  <strong>{file.name}</strong>
                  <span className="ml-2 text-zinc-500">{(file.size / 1024).toFixed(1)} KB</span>
                </>
              ) : (
                'No file selected.'
              )}
            </div>
            {file && (
              <button
                type="button"
                onClick={() => setFile(null)}
                className="text-xs text-zinc-500 hover:text-zinc-700"
              >
                Clear
              </button>
            )}
          </div>
          <div className="mt-4 flex items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <SampleTemplateButton moduleKey={config.moduleKey} />
              <OperatorGuideButton moduleKey={config.moduleKey} />
            </div>
            <UzButton onClick={handleUpload} loading={upload.isPending} disabled={!file}>
              <Cloud className="mr-1.5 h-4 w-4" />
              Upload + validate
            </UzButton>
          </div>
        </UzCard>

        {sheets.length > 0 && <SheetsOverview sheets={sheets} />}
      </div>
    </PageShell>
  );
};

const SheetsOverview = ({
  sheets,
}: {
  sheets: ReadonlyArray<ImportSheetDescriptor>;
}): React.JSX.Element => (
  <UzCard title="Expected sheets" description={`${sheets.length} sheets in the workbook.`}>
    <div className="space-y-3">
      {sheets.map((sheet) => (
        <div key={sheet.key} className="rounded-md border border-zinc-200 p-3">
          <div className="flex items-center gap-2">
            <span className="text-sm font-semibold text-zinc-900">{sheet.label}</span>
            <span className="font-mono text-xs text-zinc-500">[{sheet.key}]</span>
            {sheet.required && <UzBadge tone="destructive">required</UzBadge>}
          </div>
          <div className="mt-2 flex flex-wrap gap-1.5 text-xs text-zinc-600">
            {sheet.columns.map((c) => (
              <span
                key={c.field}
                className={`rounded border px-1.5 py-0.5 ${
                  c.required
                    ? 'border-amber-200 bg-amber-50 text-amber-800'
                    : 'border-zinc-200 bg-white'
                }`}
                title={c.hint ?? c.field}
              >
                {c.label}
              </span>
            ))}
          </div>
        </div>
      ))}
    </div>
  </UzCard>
);

const STATUS_FILTERS = [
  { value: 'ALL', label: 'All rows' },
  { value: 'INVALID', label: 'Invalid' },
  { value: 'VALID', label: 'Valid' },
  { value: 'SKIPPED', label: 'Skipped' },
  { value: 'COMMITTED', label: 'Committed' },
  { value: 'COMMIT_FAILED', label: 'Failed' },
] as const;

const ReviewStep = ({
  jobId,
  config,
}: {
  jobId: string;
  config: MultiSheetImportPageConfig;
}): React.JSX.Element => {
  const navigate = useNavigate();
  const backToList = useListReturn(config.listPath);
  const job = useImportJob(jobId);
  const modulesQuery = useImportModules();
  const moduleDescriptor = (modulesQuery.data ?? []).find(
    (m) => m.moduleKey === job.data?.moduleKey,
  );
  const sheets = useMemo(() => moduleDescriptor?.sheets ?? [], [moduleDescriptor]);
  const [activeSheet, setActiveSheet] = useState<string>(sheets[0]?.key ?? '');
  useEffect(() => {
    if (!activeSheet && sheets.length > 0) setActiveSheet(sheets[0]!.key);
  }, [activeSheet, sheets]);

  const [statusFilter, setStatusFilter] =
    useState<(typeof STATUS_FILTERS)[number]['value']>('INVALID');
  const [page, setPage] = useState(1);
  useEffect(() => {
    setPage(1);
  }, [activeSheet, statusFilter]);

  const rows = useImportRows(jobId, {
    page,
    pageSize: 25,
    sortBy: 'rowIndex',
    sortDir: 'asc',
    status: statusFilter === 'ALL' ? undefined : statusFilter,
    sheetKey: activeSheet,
  });
  const commit = useCommitImport(jobId, { invalidateOnSuccess: config.invalidateOnSuccess });
  const revalidate = useRevalidateImport(jobId);

  const [sourceRequested, setSourceRequested] = useState(false);
  const sourceQuery = useImportSourceFile(jobId, sourceRequested);
  useEffect(() => {
    if (!sourceRequested || !sourceQuery.data) return;
    const url = sourceQuery.data.url;
    if (url) window.open(url, '_self', 'noopener,noreferrer');
    setSourceRequested(false);
  }, [sourceRequested, sourceQuery.data]);

  const activeSheetDescriptor = sheets.find((s) => s.key === activeSheet);
  const columns = activeSheetDescriptor?.columns ?? [];

  const canCommit =
    !!job.data &&
    (job.data.status === 'READY' || job.data.status === 'PARTIAL') &&
    job.data.invalidRows === 0 &&
    job.data.validRows > 0;

  const ListIcon = config.ListIcon;

  if (job.isLoading || !job.data) {
    return (
      <PageShell title="Loading import job…" description="">
        <UzCard>
          <div className="flex items-center gap-2 p-6 text-sm text-zinc-500">
            <UzSpinner size="sm" /> Fetching status…
          </div>
        </UzCard>
      </PageShell>
    );
  }

  return (
    <PageShell
      title={`Import - ${job.data.fileName}`}
      description={`${job.data.totalRows} rows across ${sheets.length} sheet(s) · ${job.data.moduleKey} module`}
      actions={
        <div className="flex gap-2">
          <UzButton variant="ghost" size="sm" onClick={backToList}>
            <ListIcon className="mr-1.5 h-4 w-4" /> {config.titlePlural} list
          </UzButton>
          <UzButton
            variant="ghost"
            size="sm"
            onClick={() => navigate(`/${config.routePrefix}/imports`)}
          >
            <ArrowLeft className="mr-1.5 h-4 w-4" /> Audit log
          </UzButton>
          <UzButton
            variant="outline"
            size="sm"
            onClick={() => setSourceRequested(true)}
            loading={sourceRequested && sourceQuery.isFetching}
          >
            <Download className="mr-1.5 h-4 w-4" /> Source XLSX
          </UzButton>
          <UzButton
            variant="outline"
            size="sm"
            onClick={() => revalidate.mutateAsync().catch(() => undefined)}
            loading={revalidate.isPending}
            title="Re-check every row against the current masters and rules - clears errors fixed since upload, no re-upload needed."
          >
            <RefreshCw className="mr-1.5 h-4 w-4" /> Re-validate
          </UzButton>
          <UzButton
            onClick={() => commit.mutateAsync()}
            loading={commit.isPending}
            disabled={!canCommit}
          >
            <Check className="mr-1.5 h-4 w-4" />
            Commit {job.data.validRows} row(s)
          </UzButton>
        </div>
      }
    >
      <div className="space-y-3">
        {sourceQuery.data && sourceQuery.data.purgedAt && (
          <UzAlert tone="info">
            <AlertCircle className="mr-1 inline h-3.5 w-3.5" />
            The source XLSX was purged on{' '}
            {new Date(sourceQuery.data.purgedAt).toLocaleString('en-IN')} (past the retention
            window). Audit rows are still available below.
          </UzAlert>
        )}

        <JobSummary job={job.data} />

        {sheets.length > 0 && (
          <UzCard className="p-0!">
            <div className="flex flex-wrap gap-1 border-b border-zinc-200 px-3 pt-3">
              {sheets.map((sheet) => (
                <button
                  type="button"
                  key={sheet.key}
                  onClick={() => setActiveSheet(sheet.key)}
                  className={`rounded-t-md border border-b-0 px-3 py-1.5 text-xs font-medium transition-colors ${
                    activeSheet === sheet.key
                      ? 'border-zinc-200 bg-white text-zinc-900'
                      : 'border-transparent text-zinc-500 hover:text-zinc-700'
                  }`}
                >
                  {sheet.label}
                </button>
              ))}
            </div>

            <div className="space-y-3 p-3">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <p className="text-xs text-zinc-500">
                  Showing the <strong>{activeSheetDescriptor?.label}</strong> sheet · row index
                  refers to the row number inside that sheet.
                </p>
                <div className="w-40">
                  <UzSelect
                    name="status-filter"
                    options={
                      STATUS_FILTERS as unknown as ReadonlyArray<{ value: string; label: string }>
                    }
                    value={statusFilter}
                    onValueChange={(v) =>
                      v && setStatusFilter(v as (typeof STATUS_FILTERS)[number]['value'])
                    }
                  />
                </div>
              </div>

              {rows.isLoading ? (
                <div className="flex items-center gap-2 py-4 text-sm text-zinc-500">
                  <UzSpinner size="sm" /> Loading rows…
                </div>
              ) : (rows.data?.data ?? []).length === 0 ? (
                <div className="py-6 text-center text-sm text-zinc-500">
                  No rows on this sheet match the current filter.
                </div>
              ) : (
                <>
                  <div className="space-y-2">
                    {(rows.data?.data ?? []).map((row) => (
                      <RowCard
                        key={row.id}
                        row={row}
                        columns={columns}
                        jobId={jobId}
                        sheetKey={activeSheet}
                        summaryFields={config.summaryFieldsBySheet?.[activeSheet]}
                      />
                    ))}
                  </div>
                  {(rows.data?.pagination.totalPages ?? 1) > 1 && (
                    <div className="mt-3 flex items-center justify-between border-t border-zinc-100 pt-3 text-xs text-zinc-600">
                      <span>
                        Page {rows.data?.pagination.page} of {rows.data?.pagination.totalPages} ·{' '}
                        {rows.data?.pagination.totalItems} row
                        {rows.data?.pagination.totalItems === 1 ? '' : 's'}
                      </span>
                      <div className="flex gap-1">
                        <UzButton
                          size="sm"
                          variant="ghost"
                          onClick={() => setPage((p) => Math.max(1, p - 1))}
                          disabled={!rows.data?.pagination.hasPrev || rows.isFetching}
                        >
                          Prev
                        </UzButton>
                        <UzButton
                          size="sm"
                          variant="ghost"
                          onClick={() => setPage((p) => p + 1)}
                          disabled={!rows.data?.pagination.hasNext || rows.isFetching}
                        >
                          Next
                        </UzButton>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </UzCard>
        )}
      </div>
    </PageShell>
  );
};
